import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { getFormattedDate } from 'apps/web_forms/utils/dateTimeFormatter';

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  .date-picker-container {
    width: 50%;
  }
  .input-mask {
    border-radius: 0;
  }
  .react-datepicker__year-select {
    height: 1.5em !important;
    margin-top: 0.5em;
  }
  .react-datepicker__month-select {
    height: 1.5em !important;
    margin-top: 0.5em;
  }
  .react-datepicker-popper {
    z-index: 100;
  }
`;

const DateInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const { identifier, title, hint, hidden, validate_msg: validateMsg } = element;
  const { register, getValues } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const [{ isActive, isRequired, isReadOnly, isElementValid, initialAnswer }] = useRules(element, formContext);

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired ? 'This field is required' : false,
    })
  }, [identifier, isElementValid, validateMsg, isRequired]);
  
  const [date, setDate] = useState(null);

  useEffect(() => {
    const virtualValue = getValues(identifier)
    if(virtualValue){
      onDateChange(DateTime.fromISO(virtualValue).toJSDate(), false);
    }else if (initialAnswer) {
      onDateChange(DateTime.fromISO(initialAnswer).toJSDate(), false);
    }else {
      /**
       * This else is needed to trigger the Completion Circle calculations for Group/Subform
       * Ideally we don't want many of these triggers, but keep it in TODO list
      */
      onDateChange(null, false);
    }
  }, [initialAnswer]);

  const onDateChange = (d, isDirty = true) => {
    setDate(d);
    changeValue(d ? getFormattedDate(d, 'date') : '', isDirty);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <DateInputContainer readOnly={isReadOnly}>
        <label htmlFor={identifier}>{title}</label>
        <input
          type='text'
          hidden
          name={identifier}
          readOnly={isReadOnly}
          className={`${isReadOnly ? 'read-only' : ''}`}
        />
        <div className='date-picker-container'>
          <DatePicker
            id={identifier}
            popperClassName='react-datepicker-popper'
            selected={date}
            onChange={onDateChange}
            onFocus={removeResourceUpdateHighlight}
            customInput={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <input
                type='text'
                className={clsx(
                  'input-mask',
                  errorMessage !== undefined && 'invalid',
                  hasResourceUpdateHighlight && 'updateHighlight'
                )}
              />
            }
            placeholderText='Pick a Date'
            readOnly={isReadOnly}
            className={`${isReadOnly ? 'read-only' : ''}`}
            autoComplete='off'
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
          />
        </div>
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </DateInputContainer>
    </div>
  );
};

DateInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    initialAnswer: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(DateInput);
